import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import SectionLabel from '../components/SectionLabel';
import SessionCard from '../components/SessionCard';
import Skeleton, { EmptyState } from '../components/Skeleton';
import { fetchAgenda, hotelFromEvent, useLive } from '../data/live';
import { useNow, localHMS, isLive, isOver, todayDayIndex } from '../data/now';

export default function OverviewPage() {
  const { meeting } = useOutletContext();
  const event = meeting._event;

  const agenda = useLive(fetchAgenda, [event?.id]);
  const facts = buildFactsFromEvent(event);

  const hotel = event ? hotelFromEvent(event) : null;
  const hotelName = hotel?.name || meeting.venue;
  const hotelTagline = hotel?.tagline || '';

  // During the meeting this block is "Up next today" (what's live or still to
  // come); before it, the first three sessions of opening day.
  const now = useNow();
  const hms = localHMS(now);
  const dayKeys = agenda ? Object.keys(agenda) : [];
  const todayIdx = todayDayIndex(agenda, now);
  const isToday = todayIdx >= 0;
  const dayKey = isToday ? dayKeys[todayIdx] : dayKeys[0];
  const daySessions = dayKey ? agenda[dayKey] : [];
  const shownSessions = (isToday ? daySessions.filter((x) => !isOver(x, hms)) : daySessions).slice(0, 3);
  const welcome = event?.welcome_message?.trim();

  return (
    <div className="portal-ov">
      <div className="portal-ov-eyebrow">
        {[meeting.ordinal, meeting.city].filter(Boolean).join(' · ')}
      </div>
      <h1 className="portal-ov-h1">
        {event?.portal_headline?.trim()
          ? renderHeadline(event.portal_headline.trim())
          : <>Welcome to the <em>{meeting.ordinal}</em> {meeting.org} Meeting.</>}
      </h1>
      {welcome && <p className="portal-ov-lede">{welcome}</p>}

      <div className="portal-ov-quick">
        <Link to="../agenda" className="portal-ov-quicklink">Full agenda</Link>
        <Link to="../attendees" className="portal-ov-quicklink">Who's attending</Link>
        <Link to="../hotel" className="portal-ov-quicklink">Hotel &amp; travel</Link>
      </div>

      <div className="portal-ov-cols">
        <div className="portal-ov-main">
          {agenda === undefined && <Skeleton variant="card" count={3} />}
          {dayKey && (
            <>
              <SectionLabel right={dayKey}>{isToday ? 'Up Next Today' : 'Opening Day'}</SectionLabel>
              {shownSessions.length > 0 ? (
                <div className="portal-day-sessions">
                  {shownSessions.map((s, i) => (
                    <SessionCard key={i} session={s} live={isToday && isLive(s, hms)} />
                  ))}
                </div>
              ) : (
                <EmptyState>That’s everything on today’s agenda.</EmptyState>
              )}
            </>
          )}
        </div>

        <div className="portal-ov-side">
          <SectionLabel right="Meeting facts">At a Glance</SectionLabel>
          {facts.length > 0 && (
            <div className="portal-glance">
              {facts.map((f, i) => (
                <div key={i} className="portal-glance-row">
                  <span className="portal-glance-k">{f.k}</span>
                  <span className="portal-glance-v">{f.v}</span>
                </div>
              ))}
            </div>
          )}

          {hotelName && (
            <div className="portal-ov-hotel">
              <div className="portal-ov-hotel-head">
                <div className="portal-ov-hotel-eyebrow">Host Hotel</div>
                <div className="portal-ov-hotel-name">{hotelName}</div>
              </div>
              <div className="portal-ov-hotel-body">
                {hotelTagline && <p className="portal-ov-hotel-tagline">{hotelTagline}</p>}
                <Link to="../hotel" className="portal-ov-hotel-link">Hotel details →</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Render a Mission Control-authored headline, italicizing the ordinal (e.g.
// "48th", "1st") so it keeps the same styled accent as the hardcoded fallback.
function renderHeadline(text) {
  const m = text.match(/\b\d+(?:st|nd|rd|th)\b/i);
  if (!m) return text;
  return (
    <>
      {text.slice(0, m.index)}
      <em>{m[0]}</em>
      {text.slice(m.index + m[0].length)}
    </>
  );
}

function buildFactsFromEvent(event) {
  const facts = [];
  if (!event) return facts;
  if (event.start_date && event.end_date) {
    facts.push({ k: 'Dates', v: formatDateRange(event.start_date, event.end_date) });
  }
  if (event.venue_name) facts.push({ k: 'Venue', v: event.venue_name });
  if (event.venue_city) {
    facts.push({
      k: 'City',
      v: [event.venue_city, event.venue_state].filter(Boolean).join(', '),
    });
  }
  return facts;
}

function formatDateRange(start, end) {
  const s = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString('en-US', { month: 'short' })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}
